import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Moon, Sun, Save, Rocket, Pause, Square } from 'lucide-react'
import { useTheme } from '../../contexts/ThemeContext'

interface HeaderProps {
  workflowName?: string
  isExecuting?: boolean
  onSave?: () => void
  onKickoff?: () => void
  onPause?: () => void
  onStop?: () => void
  showWorkflowControls?: boolean
}

const Header: React.FC<HeaderProps> = ({
  workflowName = 'Untitled Workflow',
  isExecuting = false,
  onSave,
  onKickoff,
  onPause,
  onStop,
  showWorkflowControls = false,
}) => {
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()

  const steps = [
    { name: 'Create Your Crew', path: '/dashboard' },
    { name: 'Define Tasks', path: '/tasks' },
    { name: 'Kickoff', path: '/kickoff' },
  ]

  const getCurrentStepIndex = () => {
    if (location.pathname.includes('/kickoff')) return 2
    if (location.pathname.includes('/tasks')) return 1
    return 0
  }

  const currentStepIndex = getCurrentStepIndex()

  const isLandingPage = location.pathname === '/'
  const logoSize = isLandingPage ? 'h-16' : 'h-12'
  const headerPadding = isLandingPage ? 'px-12' : 'px-6'

  return (
    <header className={`header flex items-center justify-between ${headerPadding} py-4`}>
      <div className="flex items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img 
            src="/images/logos/flowran-m.png" 
            alt="f" 
            className={logoSize}
          />
          <span className="text-2xl font-bold text-gray-900 dark:text-white ml-1">lowran</span>
        </Link>

        {/* Workflow Name */}
        {showWorkflowControls && (
          <div className="ml-8 flex items-center text-sm text-gray-600 dark:text-gray-400">
            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">
              {workflowName}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-4">
        {/* Progress Steps */}
        {showWorkflowControls && (
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
            {steps.map((step, index) => (
              <React.Fragment key={step.name}>
                <span
                  className={`${
                    index === currentStepIndex
                      ? 'text-blue-900 dark:text-blue-400 font-medium'
                      : index < currentStepIndex
                      ? 'text-gray-600 dark:text-gray-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {step.name}
                </span>
                {index < steps.length - 1 && (
                  <svg
                    className="w-3 h-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Execution Status */}
        {isExecuting && (
          <div className="flex items-center space-x-2 text-sm">
            <div className="flex items-center px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="status-indicator status-running mr-2"></div>
              <span className="text-green-700 dark:text-green-400 font-medium">
                Running
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          {/* Theme Toggle - Only show inside the app, not on landing page */}
          {!isLandingPage && (
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>
          )}

          {/* Workflow Controls */}
          {showWorkflowControls && (
            <>
              {isExecuting ? (
                <>
                  <button
                    onClick={onPause}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <Pause className="w-4 h-4" />
                    <span>Pause</span>
                  </button>
                  <button
                    onClick={onStop}
                    className="btn flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Square className="w-4 h-4" />
                    <span>Stop</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onSave}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save</span>
                  </button>
                  <button
                    onClick={onKickoff}
                    className="btn btn-primary flex items-center space-x-2"
                  >
                    <Rocket className="w-4 h-4" />
                    <span>Kickoff</span>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
